/**
 * `<CoordinateModeBadge />` — shared chip indicating whether a 3D / point-cloud
 * layer lives in a real-world georeferenced frame or a local model frame.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.5 +
 *     §9 P0 cross-cutting requirement ("every 3D / point cloud card must show
 *     the project anchor kind and the per-layer coordinate mode").
 *   - `geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md` —
 *     `gps_off` reserved for "GNSS float / autonomous / no fix"; reused for
 *     local-frame to communicate "no real-world GNSS reference". `public`
 *     reserved for "earth / globe view" and reused for georeferenced.
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 — every state
 *     must communicate with both color and a non-color cue; this badge pairs
 *     the chip variant (info vs warning) with the Material Symbol and a
 *     human label, never colour alone.
 *
 * Consumed by:
 *   - `<PointCloudToolCard />` (WP-5).
 *   - `<ThreeDModelToolCard />` (WP-6, scheduled).
 */

import type { Layer } from '@geosoft/viewer-sdk';
import { Chip } from '@/design';

/**
 * `'georeferenced'` — real-world projected frame.
 * `'local'` — model-local cartesian frame (no CRS).
 * `'local-grid'` — survey/site grid (e.g. mine grid, refinery local grid)
 *   identified by a `frameId` (added in WP-7 for geophysics + bathymetry
 *   surfaces; treated as a sub-variant of `local` for icon/variant purposes
 *   so the playbook non-color cue is preserved).
 */
export type CoordinateMode = 'georeferenced' | 'local' | 'local-grid';

export interface CoordinateModeBadgeProps {
  /** Source coordinate frame. `'local'` / `'local-grid'` triggers the `gps_off` cue. */
  mode: CoordinateMode;
  /** Optional context (e.g. layer label) appended to the accessible name. */
  context?: string;
  /** Optional grid identifier (e.g. `"refinery-grid-A"`). When supplied for a
   *  `'local-grid'` mode, the chip label becomes `Grid: <frameId>` and the
   *  accessible name surfaces the same identifier. */
  frameId?: string;
  /** Optional override for the visible label. Falls back to the canonical
   *  "Georeferenced" / "Local frame" / "Grid: …" string from the playbook. */
  label?: string;
  'data-testid'?: string;
}

const MODE_TABLE = {
  georeferenced: {
    icon: 'public',
    label: 'Georeferenced',
    variant: 'info',
  },
  local: {
    icon: 'gps_off',
    label: 'Local frame',
    variant: 'warning',
  },
  'local-grid': {
    icon: 'gps_off',
    label: 'Local grid',
    variant: 'warning',
  },
} as const;

export function CoordinateModeBadge({
  mode,
  context,
  frameId,
  label,
  'data-testid': testId,
}: CoordinateModeBadgeProps) {
  const entry = MODE_TABLE[mode];
  const fallbackLabel =
    mode === 'local-grid' && frameId ? `Grid: ${frameId}` : entry.label;
  const visibleLabel = label ?? fallbackLabel;
  const ariaContext =
    context ?? (mode === 'local-grid' && frameId ? frameId : undefined);
  const ariaLabel = ariaContext
    ? `${visibleLabel} — ${ariaContext}`
    : `Coordinate mode: ${visibleLabel}`;

  return (
    <Chip
      icon={entry.icon}
      label={visibleLabel}
      variant={entry.variant}
      aria-label={ariaLabel}
      data-testid={testId ?? `coordinate-mode-badge-${mode}`}
    />
  );
}

/**
 * Pure helper: derives the coordinate mode from a `@geosoft/viewer-sdk`
 * Layer.  Mirrors the existing `TwinShell` `layerCoordinateMode` logic so the
 * badge and the existing layer-eligibility logic stay in lock-step.
 *
 * A layer is considered `local` when:
 *   - `layer.style.coordinateMode === 'local'`, OR
 *   - `layer.crs` is empty / `"local"` / `"EPSG:0"`.
 *
 * Otherwise the badge defaults to `georeferenced`.
 */
export function coordinateModeForLayer(
  layer: Layer | null | undefined,
): CoordinateMode {
  if (!layer) return 'georeferenced';
  const style = layer.style as { coordinateMode?: unknown } | undefined;
  if (typeof style?.coordinateMode === 'string') {
    return style.coordinateMode === 'georeferenced' ? 'georeferenced' : 'local';
  }
  const crs = (layer.crs ?? '').trim().toLowerCase();
  if (crs === '' || crs === 'local' || crs === 'epsg:0') return 'local';
  return 'georeferenced';
}
